import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BookingStatus } from "@/lib/types";
import { Calendar, Search, Filter, Loader2, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { apiRequest } from '@/lib/api';
import GuestLayout from "@/components/layout/GuestLayout";

// Define Booking type locally
interface Booking {
  id: number;
  propertyId: number;
  propertyTitle?: string;
  property?: { title?: string };
  checkIn: string;
  checkOut: string;
  guestCount: number;
  totalAmount: number;
  status: string;
  createdAt?: string;
}

export default function GuestBookingsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("upcoming");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch bookings from API
  const { data: bookingsData = [], isLoading } = useQuery<Booking[]>({
    queryKey: ['guest/bookings'],
    enabled: !!user,
    queryFn: async () => {
      try {
        const res = await apiRequest('GET', 'guest/bookings');
        return res as Booking[];
      } catch (err: any) {
        toast({
          title: "Error loading bookings",
          description: err.message || "Failed to load bookings data",
          variant: "destructive",
        });
        return [];
      }
    }
  });

  // Group bookings by status
  const processingBookings = (bookings: Booking[] = []) => {
    const grouped: { upcoming: Booking[]; completed: Booking[]; cancelled: Booking[] } = {
      upcoming: [],
      completed: [],
      cancelled: []
    };
    const today = new Date();
    bookings.forEach((booking) => {
      const checkIn = new Date(booking.checkIn);
      const checkOut = new Date(booking.checkOut);
      if (booking.status === BookingStatus.CANCELLED) {
        grouped.cancelled.push(booking);
      } else if (booking.status === BookingStatus.COMPLETED || 
                (booking.status === BookingStatus.CONFIRMED && checkOut < today)) {
        grouped.completed.push(booking);
      } else {
        grouped.upcoming.push(booking);
      }
    });
    return grouped;
  };

  const bookings = processingBookings(bookingsData);

  // Filter bookings by search and status
  const filterBookings = (bookingList: Booking[] = []) => {
    return bookingList.filter((booking) => {
      const propertyTitle = booking.propertyTitle || booking.property?.title || '';
      const searchMatch =
        searchTerm === "" ||
        propertyTitle.toLowerCase().includes(searchTerm.toLowerCase());
      const statusMatch =
        statusFilter === "all" ||
        booking.status.toLowerCase() === statusFilter.toLowerCase();
      return searchMatch && statusMatch;
    });
  };

  const getBookingsForTab = (tab: string) => {
    switch (tab) {
      case "upcoming":
        return [...(bookings.upcoming || [])].sort((a, b) =>
          new Date(a.checkIn).getTime() - new Date(b.checkIn).getTime());
      case "completed":
        return [...(bookings.completed || [])].sort((a, b) =>
          new Date(b.checkOut).getTime() - new Date(a.checkOut).getTime());
      case "cancelled":
        return bookings.cancelled || [];
      case "all":
        return [...(bookings.upcoming || []), ...(bookings.completed || []), ...(bookings.cancelled || [])];
      default:
        return [];
    }
  };

  const filteredBookings = filterBookings(getBookingsForTab(activeTab));

  const getStatusBadge = (status: BookingStatus) => {
    switch (status) {
      case BookingStatus.PENDING:
        return (
          <Badge variant="outline" className="flex items-center gap-1 text-yellow-600 bg-yellow-50 border-yellow-200">
            <AlertTriangle className="h-3 w-3" />
            <span>Pending</span>
          </Badge>
        );
      case BookingStatus.CONFIRMED:
        return (
          <Badge variant="outline" className="flex items-center gap-1 text-green-600 bg-green-50 border-green-200">
            <CheckCircle className="h-3 w-3" />
            <span>Confirmed</span>
          </Badge>
        );
      case BookingStatus.COMPLETED:
        return (
          <Badge variant="outline" className="flex items-center gap-1 text-blue-600 bg-blue-50 border-blue-200">
            <CheckCircle className="h-3 w-3" />
            <span>Completed</span>
          </Badge>
        );
      case BookingStatus.CANCELLED:
        return (
          <Badge variant="outline" className="flex items-center gap-1 text-red-600 bg-red-50 border-red-200">
            <XCircle className="h-3 w-3" />
            <span>Cancelled</span>
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            <span>Unknown</span>
          </Badge>
        );
    }
  };

  return (
    <GuestLayout>
      <div className="container py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-1">My Bookings</h1>
            <p className="text-muted-foreground">View all your stays and reservations</p>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search by property..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="upcoming">Upcoming ({(bookings?.upcoming || []).length})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({(bookings?.completed || []).length})</TabsTrigger>
            <TabsTrigger value="cancelled">Cancelled ({(bookings?.cancelled || []).length})</TabsTrigger>
            <TabsTrigger value="all">All Bookings ({(bookings?.upcoming || []).length + (bookings?.completed || []).length + (bookings?.cancelled || []).length})</TabsTrigger>
          </TabsList>
        </Tabs>

        <Card>
          <CardHeader className="px-6">
            <CardTitle>Booking Details</CardTitle>
            <CardDescription>View all your bookings and stays</CardDescription>
          </CardHeader>
          <CardContent className="px-6">
            {isLoading ? (
              <div className="flex justify-center items-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredBookings.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking ID</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Check-in</TableHead>
                    <TableHead>Check-out</TableHead>
                    <TableHead>Guests</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Booked on</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell className="font-medium text-blue-600 underline cursor-pointer" onClick={() => window.location.href = `/bookings/${booking.id}/confirmation`}>
                        {booking.id}
                      </TableCell>
                      <TableCell className="font-medium">
                        <a
                          href={`/properties/${booking.propertyId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline hover:text-blue-800"
                        >
                          {booking.propertyTitle || booking.property?.title || `Property #${booking.propertyId}`}
                        </a>
                      </TableCell>
                      <TableCell>{getStatusBadge(booking.status as BookingStatus)}</TableCell>
                      <TableCell>{format(new Date(booking.checkIn), 'MMM d, yyyy')}</TableCell>
                      <TableCell>{format(new Date(booking.checkOut), 'MMM d, yyyy')}</TableCell>
                      <TableCell>{booking.guestCount}</TableCell>
                      <TableCell>
                        {booking.totalAmount ?
                          new Intl.NumberFormat('en-AE', {
                            style: 'currency',
                            currency: 'AED',
                            minimumFractionDigits: 0,
                          }).format(booking.totalAmount) :
                          'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        {booking.createdAt ? (
                          <span title={new Date(booking.createdAt).toLocaleString()}>
                            {new Date(booking.createdAt).toLocaleDateString()}
                          </span>
                        ) : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No bookings found matching your filters.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </GuestLayout>
  );
} 